import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Chip,
    Grid,
    Divider,
    Paper,
    IconButton
} from '@mui/material';
import {
    Close as CloseIcon,
    Schedule as PendingIcon,
    CheckCircle as CheckIcon,
    SwapHoriz as ReassignIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import { format } from 'date-fns';

const STATUS_COLORS = {
    PENDING: 'warning',
    RESPONDED: 'success',
    REASSIGNED: 'info',
    COMPLETED: 'default',
    CANCELLED: 'error'
};

const STATUS_ICONS = {
    PENDING: <PendingIcon fontSize="small" />,
    RESPONDED: <CheckIcon fontSize="small" />,
    REASSIGNED: <ReassignIcon fontSize="small" />
};

const SupportDetailDialog = ({ open, onClose, request }) => {
    if (!request) return null;

    const formatDate = (dateValue) => {
        if (!dateValue) return '-';
        try {
            let date;
            if (Array.isArray(dateValue)) {
                const [year, month, day, hour = 0, minute = 0, second = 0] = dateValue;
                date = new Date(year, month - 1, day, hour, minute, second);
            } else if (typeof dateValue === 'string') {
                date = new Date(dateValue);
            } else {
                return String(dateValue);
            }
            if (isNaN(date.getTime())) return String(dateValue);
            return format(date, 'MMM dd, yyyy HH:mm');
        } catch {
            return String(dateValue);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                <Box display="flex" alignItems="center" gap={1.5}>
                    <Typography variant="h6" fontWeight="bold">
                        Support Request Details
                    </Typography>
                    <Chip
                        icon={STATUS_ICONS[request.status]}
                        label={request.status}
                        color={STATUS_COLORS[request.status] || 'default'}
                        size="small"
                    />
                    {request.isOverdue && (
                        <Chip
                            icon={<WarningIcon />}
                            label="Overdue"
                            color="error"
                            size="small"
                            variant="outlined"
                        />
                    )}
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ pt: 3 }}>
                {/* Provider Information */}
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: 'primary.main' }}>
                    Support Provider
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="textSecondary" display="block">
                                Name
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                                {request.providerUsername}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="textSecondary" display="block">
                                Email
                            </Typography>
                            <Typography variant="body1">
                                {request.providerEmail}
                            </Typography>
                        </Grid>
                        {request.region && (
                            <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="textSecondary" display="block">
                                    Region
                                </Typography>
                                <Typography variant="body1">
                                    {request.region?.name || '-'}
                                </Typography>
                            </Grid>
                        )}
                        {request.jobTitle && (
                            <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="textSecondary" display="block">
                                    Job Title
                                </Typography>
                                <Typography variant="body1">
                                    {request.jobTitle}
                                </Typography>
                            </Grid>
                        )}
                        {request.userAutoCreated && (
                            <Grid item xs={12}>
                                <Chip label="Auto-created Account" size="small" color="warning" variant="outlined" />
                            </Grid>
                        )}
                    </Grid>
                </Paper>

                {/* Support Query */}
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: 'primary.main' }}>
                    Support Query
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {request.supportQuery}
                    </Typography>
                </Paper>

                {/* Support Response */}
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: 'primary.main' }}>
                    Support Response
                </Typography>
                {request.supportResponse ? (
                    <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'success.50', borderColor: 'success.light' }}>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {request.supportResponse}
                        </Typography>
                        {request.responseDate && (
                            <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                                Responded on: {formatDate(request.responseDate)}
                            </Typography>
                        )}
                    </Paper>
                ) : (
                    <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'warning.50', borderColor: 'warning.light' }}>
                        <Typography variant="body2" color="textSecondary" fontStyle="italic">
                            No response has been provided yet.
                        </Typography>
                    </Paper>
                )}

                {/* Timestamps */}
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: 'primary.main' }}>
                    Timeline
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="textSecondary" display="block">
                                Created
                            </Typography>
                            <Typography variant="body2">
                                {formatDate(request.createdAt)}
                            </Typography>
                        </Grid>
                        {request.lastModifiedAt && (
                            <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="textSecondary" display="block">
                                    Last Modified
                                </Typography>
                                <Typography variant="body2">
                                    {formatDate(request.lastModifiedAt)}
                                </Typography>
                            </Grid>
                        )}
                        {request.responseDate && (
                            <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="textSecondary" display="block">
                                    Response Date
                                </Typography>
                                <Typography variant="body2">
                                    {formatDate(request.responseDate)}
                                </Typography>
                            </Grid>
                        )}
                        {request.reassignmentRequestedAt && (
                            <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="textSecondary" display="block">
                                    Reassignment Requested
                                </Typography>
                                <Typography variant="body2">
                                    {formatDate(request.reassignmentRequestedAt)}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </Paper>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} variant="outlined">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SupportDetailDialog;
